import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, AlertTriangle, Info, Package } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

const ICONS = {
  low_stock: AlertTriangle,
  info: Info,
  default: Bell,
};

const COLORS = {
  low_stock: 'text-amber-600 bg-amber-50',
  info: 'text-blue-600 bg-blue-50',
  default: 'text-slate-600 bg-slate-50',
};

const NotificationBell = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => { fetchNotifications(); const iv = setInterval(fetchNotifications, 60000); return () => clearInterval(iv); }, []);
  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications?limit=10');
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch {}
  };

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); fetchNotifications(); } catch {}
  };

  const markAllRead = async () => {
    try { await api.post('/notifications/mark-all-read'); fetchNotifications(); } catch {}
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button onClick={() => setOpen(!open)} className="relative p-2 hover:bg-slate-100 rounded-sm" data-testid="bell-btn">
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
            data-testid="unread-count">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 mt-2 w-80 bg-white border-2 border-slate-100 shadow-lg z-50" data-testid="notification-panel">
            <div className="p-3 border-b border-slate-100 flex items-center justify-between">
              <h4 className="font-semibold text-[#1e3a5f] text-sm">{t('notif_title') || 'Notifications'}</h4>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-[#22c55e] hover:underline" data-testid="mark-all-read">
                  {t('notif_mark_all') || 'Mark all read'}
                </button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">{t('notif_empty') || 'No notifications'}</div>
              ) : (
                notifications.map(n => {
                  const NIcon = ICONS[n.type] || ICONS.default;
                  const color = COLORS[n.type] || COLORS.default;
                  return (
                    <div key={n.id}
                      className={`flex gap-3 p-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${!n.read ? 'bg-blue-50/30' : ''}`}
                      onClick={() => !n.read && markRead(n.id)}
                      data-testid={`notification-${n.id}`}>
                      <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${color}`}>
                        <NIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1e3a5f] leading-tight">{n.title}</p>
                        <p className="text-xs text-slate-400 mt-0.5 truncate">{n.message}</p>
                      </div>
                      <div className="flex flex-col items-end flex-shrink-0">
                        <span className="text-[10px] text-slate-400">{timeAgo(n.created_at)}</span>
                        {!n.read && <span className="w-2 h-2 bg-[#22c55e] rounded-full mt-1" />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
